import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { setup2FA, confirm2FA } from '../services/api';
import { useAuth } from '../hooks/useAuth';

/**
 * Reached two ways: (1) an already-logged-in user opting in from Account
 * Security — no tempToken, the session cookie carries auth; (2) a
 * just-logged-in user whose admin has forced 2FA for everyone — no session
 * exists yet, so a tempToken from the login response is threaded through
 * via router state instead (see backend routes/auth.js resolveTwoFactorSubject).
 */
export default function TwoFactorSetupPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const tempToken = location.state?.tempToken || null;
  const forced = location.state?.forced || false;

  const [qrCodeDataUri, setQrCodeDataUri] = useState(null);
  const [secretBase32, setSecretBase32] = useState(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    setup2FA(tempToken)
      .then((data) => {
        setQrCodeDataUri(data.qrCodeDataUri);
        setSecretBase32(data.secretBase32);
      })
      .catch((err) => setError(err.response?.data?.error || 'Failed to start two-factor setup'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError('');
    setConfirming(true);
    try {
      const data = await confirm2FA(code, tempToken);
      setRecoveryCodes(data.recoveryCodes);
      await refreshUser();
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code');
    } finally {
      setConfirming(false);
    }
  };

  const handleDone = () => navigate(forced ? '/receipts' : '/account/security');

  const wrapperStyle = forced
    ? { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-bg)', padding: '1rem', boxSizing: 'border-box' }
    : { maxWidth: 480 };

  if (loading) {
    return <div style={forced ? wrapperStyle : { padding: '2rem' }}>Loading…</div>;
  }

  if (recoveryCodes) {
    return (
      <div style={wrapperStyle}>
        <div className="card" style={{ width: '100%', maxWidth: 420 }}>
          <h2 style={{ marginTop: 0 }}>Two-factor authentication enabled</h2>
          <p className="muted">
            Save these recovery codes somewhere safe. Each one can be used once, instead of
            your authenticator app, if you ever lose access to it. They won't be shown again.
          </p>
          <pre style={{ background: 'var(--color-bg)', padding: '1rem', borderRadius: 6, fontSize: 14, lineHeight: 1.8, overflowX: 'auto' }}>
            {recoveryCodes.join('\n')}
          </pre>
          <button className="btn-primary" onClick={handleDone} style={{ width: '100%', marginTop: '1rem', padding: '0.6rem' }}>
            I've saved these — continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={wrapperStyle}>
      <div className="card" style={{ width: '100%', maxWidth: 420 }}>
        <h2 style={{ marginTop: 0 }}>
          {forced ? 'Set up two-factor authentication' : 'Enable two-factor authentication'}
        </h2>
        {forced && (
          <p className="muted" style={{ fontSize: 13 }}>
            Your administrator requires two-factor authentication for all accounts before you can continue.
          </p>
        )}
        <p className="muted" style={{ fontSize: 13 }}>
          Scan this with an authenticator app (Google Authenticator, Authy, 1Password, etc.):
        </p>
        {qrCodeDataUri && (
          <img
            src={qrCodeDataUri}
            alt="Two-factor authentication QR code"
            style={{ display: 'block', margin: '1rem auto', maxWidth: 220, width: '100%' }}
          />
        )}
        {secretBase32 && (
          <p className="muted" style={{ fontSize: 12, textAlign: 'center', wordBreak: 'break-all' }}>
            Can't scan? Enter manually: <code>{secretBase32}</code>
          </p>
        )}
        <form onSubmit={handleConfirm} style={{ marginTop: '1rem' }}>
          <label htmlFor="2fa-setup-code" style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Enter the 6-digit code</label>
          <input
            id="2fa-setup-code"
            name="otp"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            autoFocus
          />
          {error && <p className="error">{error}</p>}
          <button
            type="submit"
            className="btn-primary"
            disabled={confirming}
            style={{ width: '100%', marginTop: '1rem', padding: '0.6rem' }}
          >
            {confirming ? 'Verifying…' : 'Confirm & enable'}
          </button>
        </form>
      </div>
    </div>
  );
}
